/**
 * Org context — cross-lab access (super-admins, Phase 24) AND multi-membership switching for any
 * user (Phase 25).
 *
 * Two kinds of caller act in more than one lab:
 *  - A platform SUPER-ADMIN who JOINed extra labs (Phase 24). Their active lab is server-tracked:
 *    switching POSTs `/admin/active-org` (membership-validated + audited), and `/me/active-org`
 *    reports it. When the active lab differs from their HOME lab the amber "Acting in" banner shows
 *    — a super-admin-specific environmental signal (`isActingInForeignLab`).
 *  - A regular user who simply BELONGS to more than one lab (Phase 25). There is no server-side
 *    active-org persistence for them (POST `/admin/active-org` is super-admin-only); their switch is
 *    purely client-side — write `localStorage["eddkit.active_org"]` and invalidate the cache so every
 *    org-scoped query re-fetches. The backend re-scopes off the `X-Active-Org` header, which it
 *    validates against `UserOrg` on every request. They NEVER see the acting-in banner (switching
 *    among their own legitimate labs is normal, not a cross-lab intrusion — UI-SPEC §3.1 / Pitfall 5).
 *
 * `memberships` is now fetched for EVERY authenticated user (Phase 25 dropped the Phase 24
 * super-admin-only gate) so the nav OrgSwitcher can surface to any user with >1 membership. The
 * active lab resolves SILENTLY at boot (ROLE-04): last-used (`localStorage`) → an admin-role
 * membership → the first membership — no chooser dialog. A stale/removed stored lab heals to a valid
 * membership. A user with zero memberships is a data error: the provider renders an ErrorState
 * (contact-admin) rather than logging them out — their session is still valid.
 *
 * `getActiveOrgId()` is exported as a STANDALONE function (a plain localStorage read, no React)
 * so `src/api/client.ts` can import it to inject the `X-Active-Org` header without any
 * provider-order coupling. The server validates it against `UserOrg` every request — a forged
 * localStorage value grants nothing.
 *
 * NOTE on provider order: per UI-SPEC §4.3 `OrgProvider` wraps `LocationProvider` which wraps
 * `PermissionsProvider`, so this file CANNOT call `usePermissions()` (it sits above that
 * provider). Instead it reads super-admin status from the same `/me/permissions` query
 * `PermissionsContext` derives it from (`useMyPermissions`) and the home org id from the auth
 * `/me` user (`useAuth().user.org_id`) — both are order-independent (they only need the
 * QueryClient / AuthProvider at the app root, which sit above OrgProvider).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { useMyPermissions } from "../api/permissions";
import {
  useActiveOrg,
  useMyMemberships,
  useSwitchOrg,
  type ActiveOrg,
  type OrgMembership,
} from "../api/adminOrgs";
import { Badge } from "../components/Badge";
import { ErrorState } from "../components/EmptyState";
import { useToast } from "../components/Toast";
// The active-org storage primitives live in a leaf module (no React imports) so `client.ts` can
// import `getActiveOrgId` without a module-init cycle through this file. See activeOrg.ts.
import { getActiveOrgId, readStoredId, writeStoredId } from "./activeOrg";

// Re-exported so existing call sites can import `getActiveOrgId` from either OrgContext or activeOrg.
export { getActiveOrgId };

interface OrgState {
  /** The labs the caller belongs to (from GET /me/memberships). Now fetched for ALL users. */
  memberships: OrgMembership[];
  /** The lab the caller is currently acting in (super-admin: from GET /me/active-org; multi-membership
   *  user: synthesized from the resolved active membership), or null. */
  activeOrg: ActiveOrg | null;
  /** The resolved active lab's org id (the value the X-Active-Org header carries), or null. */
  activeOrgId: string | null;
  /** The caller's home org id (from the auth /me user). */
  homeOrgId: string | null;
  /** True when the active lab differs from the home lab AND the caller is a super-admin (drives the
   *  acting-in banner). Stays false for a regular user's own multi-membership switches. */
  isActingInForeignLab: boolean;
  isLoading: boolean;
  /** Switch the active lab. Super-admin: POST /admin/active-org, persist, invalidate. Multi-membership
   *  user: persist + invalidate (client-side; the backend re-scopes off X-Active-Org). */
  switchOrg: (orgId: string) => Promise<void>;
  /** Return to the home lab (= switchOrg(homeOrgId)). */
  exitToHomeOrg: () => Promise<void>;
}

const OrgContext = createContext<OrgState | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const homeOrgId = user?.org_id ?? null;

  // Super-admin status comes from the /me/permissions query (the same source PermissionsContext
  // uses) — NOT usePermissions(), since OrgProvider sits above PermissionsProvider.
  const { data: perms } = useMyPermissions();
  const isSuperAdmin = perms?.is_super_admin ?? false;

  // Memberships are fetched for EVERY authenticated user (Phase 25): the switcher surfaces to any
  // multi-membership user, not just super-admins. The active-org read stays super-admin-only — only
  // they have a server-tracked active lab (and thus an acting-in banner); regular users resolve it
  // client-side from memberships + localStorage (ROLE-04 below).
  const activeOrgQ = useActiveOrg(isSuperAdmin);
  const membershipsQ = useMyMemberships();
  const switchMutation = useSwitchOrg();
  const queryClient = useQueryClient();

  const memberships = useMemo<OrgMembership[]>(
    () => membershipsQ.data ?? [],
    [membershipsQ.data],
  );

  // ROLE-04 silent active-lab resolution (multi-membership users). Mirrors LocationContext's
  // heal-on-mount: the stored choice if it's still a valid membership, else an admin-role membership
  // (first if several), else the first membership. Super-admins keep the server-tracked active org.
  const resolvedActiveId = useMemo<string | null>(() => {
    if (memberships.length === 0) return null;
    const stored = readStoredId();
    if (stored && memberships.some((m) => m.org_id === stored)) return stored;
    const adminMembership = memberships.find((m) => m.role === "admin");
    return (adminMembership ?? memberships[0]).org_id;
  }, [memberships]);

  // For super-admins the active lab is whatever the server reports (POST /admin/active-org tracks it);
  // for everyone else it's the client-resolved membership.
  const serverActiveOrg = isSuperAdmin ? (activeOrgQ.data ?? null) : null;
  const activeOrgId = isSuperAdmin
    ? (serverActiveOrg?.id ?? resolvedActiveId)
    : resolvedActiveId;

  // The active lab as an ActiveOrg: the server row for super-admins, else synthesized from the
  // resolved membership (so the switcher highlight + nav label reflect the client-side choice).
  const activeOrg = useMemo<ActiveOrg | null>(() => {
    if (isSuperAdmin) return serverActiveOrg;
    if (!activeOrgId) return null;
    const m = memberships.find((mm) => mm.org_id === activeOrgId);
    return m ? { id: m.org_id, name: m.org_name, role: m.role } : null;
  }, [isSuperAdmin, serverActiveOrg, activeOrgId, memberships]);

  // Persist the resolved active org id so it survives reloads and the X-Active-Org header stays in
  // sync with the lab the user is acting in. (Super-admins also persist the server-resolved id.)
  useEffect(() => {
    if (activeOrgId && activeOrgId !== readStoredId()) {
      writeStoredId(activeOrgId);
    }
  }, [activeOrgId]);

  // Heal-on-stale: a stored active id that is NOT among the caller's memberships is stale (a removed
  // membership, or left over from another account). The resolution above already falls back to a
  // valid membership; clear the dead id and (once) tell the user their lab is gone. We only warn when
  // there IS a fallback to land on — an empty membership list is handled by the ErrorState branch.
  const warnedStaleRef = useRef(false);
  useEffect(() => {
    if (membershipsQ.isLoading || memberships.length === 0) return;
    const stored = readStoredId();
    if (stored && !memberships.some((m) => m.org_id === stored)) {
      writeStoredId(activeOrgId);
      if (!warnedStaleRef.current) {
        warnedStaleRef.current = true;
        toast.info(t("org.labUnavailable"));
      }
    }
  }, [membershipsQ.isLoading, memberships, activeOrgId, toast, t]);

  const value = useMemo<OrgState>(() => {
    const switchOrg = async (orgId: string): Promise<void> => {
      // Super-admins switch via the server (validated + audited); regular multi-membership users
      // switch client-side only — POST /admin/active-org is super-admin-gated (404 otherwise), and the
      // backend already re-scopes every request off the validated X-Active-Org header.
      if (isSuperAdmin) {
        await switchMutation.mutateAsync(orgId);
      }
      writeStoredId(orgId);
      // Re-scope EVERYTHING to the new active lab (no filter).
      await queryClient.invalidateQueries();
    };

    return {
      memberships,
      activeOrg,
      activeOrgId,
      homeOrgId,
      // Banner is super-admin-only: a regular user's own multi-membership switch never triggers it.
      isActingInForeignLab: isSuperAdmin && !!activeOrg && activeOrg.id !== homeOrgId,
      isLoading: (isSuperAdmin && activeOrgQ.isLoading) || membershipsQ.isLoading,
      switchOrg,
      exitToHomeOrg: async () => {
        if (homeOrgId) await switchOrg(homeOrgId);
      },
    };
  }, [
    isSuperAdmin,
    memberships,
    activeOrg,
    activeOrgId,
    homeOrgId,
    activeOrgQ.isLoading,
    membershipsQ.isLoading,
    switchMutation,
    queryClient,
  ]);

  // Zero memberships is a data error (every user must belong to ≥1 lab post-Phase-23). Surface a
  // contact-admin ErrorState INSTEAD of the app — but never log the user out (their session is valid).
  // Only after the memberships query has actually resolved, so we don't flash it during the fetch.
  if (!membershipsQ.isLoading && !membershipsQ.isError && memberships.length === 0) {
    return (
      <div className="page stack">
        <div className="card">
          <ErrorState title={t("org.noMembershipsError")} />
        </div>
      </div>
    );
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useCurrentOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useCurrentOrg must be used within an OrgProvider");
  }
  return ctx;
}

/**
 * Whether the caller is an admin **in the active lab** — the SINGLE source of truth for admin-gated
 * UI (Phase 25). It is the role on the caller's active `UserOrg` membership OR platform super-admin
 * (an admin anywhere). The global `User.role` was dropped in v2.5, so the legacy
 * `useAuth().user?.role === "admin"` is now always false; every gated surface must use this hook.
 */
export function useIsAdmin(): boolean {
  const { memberships, activeOrgId } = useCurrentOrg();
  const { data: perms } = useMyPermissions();
  return (
    memberships.find((m) => m.org_id === activeOrgId)?.role === "admin" ||
    (perms?.is_super_admin ?? false)
  );
}

/**
 * Lab switcher — mirrors `LocationSwitcher` exactly (same CSS classes, same keyboard / click-outside /
 * Escape behaviour). Lists the caller's MEMBERSHIPS (the labs they belong to). Selecting a lab
 * switches the active org; when a super-admin is acting in a foreign lab a bottom "Return to my lab"
 * item exits to the home lab. Surfaces to ANY user with more than one membership (the Layout render
 * gate is `memberships.length > 1`); each item carries a per-lab role badge (Phase 25).
 *
 * Distinct icon ◎ (U+25CE) vs LocationSwitcher's ◉ (U+25C9) so the two nav controls read apart
 * at a glance.
 */
export function OrgSwitcher() {
  const { t } = useTranslation();
  const { memberships, activeOrg, isActingInForeignLab, isLoading, switchOrg, exitToHomeOrg } =
    useCurrentOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside + Escape to close (mirrors LocationSwitcher).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = isLoading
    ? t("sadmin.switch.loading")
    : (activeOrg?.name ?? t("sadmin.switch.label"));

  return (
    <div className="location-switch" ref={ref}>
      <button
        type="button"
        className={`location-switch__btn ${open ? "location-switch__btn--open" : ""}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("sadmin.switch.label")}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="location-switch__icon" aria-hidden="true">
          ◎
        </span>
        <span className="location-switch__name">{label}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="location-switch__menu" role="menu">
          {memberships.map((m) => {
            const isActive = activeOrg?.id === m.org_id;
            return (
              <button
                key={m.org_id}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                aria-label={t("org.switcherItemLabel", { org: m.org_name, role: m.role })}
                className={`location-switch__item location-switch__item--with-role ${
                  isActive ? "location-switch__item--active" : ""
                }`.trim()}
                onClick={() => {
                  setOpen(false);
                  void switchOrg(m.org_id);
                }}
              >
                <span className="location-switch__item-label">{m.org_name}</span>
                <Badge tone={m.role === "admin" ? "success" : "default"}>{m.role}</Badge>
              </button>
            );
          })}
          {isActingInForeignLab && (
            <button
              type="button"
              role="menuitem"
              className="location-switch__item"
              onClick={() => {
                setOpen(false);
                void exitToHomeOrg();
              }}
            >
              <span>{t("sadmin.switch.returnToHome")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
